import { useMemo } from 'react';
import {
  CoverageBadge,
  DensityBar,
  Icon,
  Panel,
  Screen,
  Stat,
} from '@garnet/ui';
import type { Client } from '../data/types.js';
import {
  auditForClient,
  clientCells,
  gapsFor,
  KIND_DOT,
  watchForClient,
  type ClientCell,
  type StudioContext,
} from '../studio.js';
import { StateBlock } from '../components.js';

function domShort(ctx: StudioContext, key: string): string {
  return ctx.api.domains().find((d) => d.key === key)?.short ?? key;
}

function ClientSubMatrix({ ctx, client }: { ctx: StudioContext; client: Client }) {
  const { api } = ctx;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `92px repeat(${client.domains.length}, minmax(120px, 1fr))`,
          minWidth: 220,
        }}
      >
        <div style={{ borderBottom: '1px solid var(--border)' }} />
        {client.domains.map((dk) => (
          <div
            key={dk}
            style={{
              padding: '8px 10px',
              borderBottom: '1px solid var(--border)',
              borderLeft: '1px solid var(--border-subtle)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg-1)',
            }}
          >
            {domShort(ctx, dk)}
          </div>
        ))}
        {client.jurisdictions.map((jc) => {
          const j = api.jurisdictions().find((x) => x.code === jc);
          return (
            <div key={jc} style={{ display: 'contents' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '0 10px',
                  borderBottom: '1px solid var(--border-subtle)',
                  borderRight: '1px solid var(--border)',
                }}
              >
                <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--garnet)' }}>
                  {jc}
                </span>
                <span
                  className="metatext"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {j?.name}
                </span>
              </div>
              {client.domains.map((dk) => {
                const key = `${jc}:${dk}`;
                const c = api.coverageCell(key);
                if (!c) return <div key={key} />;
                return (
                  <div
                    key={key}
                    className={`cov-cell ${c.state}`}
                    onClick={() => ctx.openStore(key)}
                    role="button"
                    style={{ minHeight: 58, padding: '8px 10px', borderLeft: '1px solid var(--border-subtle)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span
                        className="mono"
                        style={{
                          fontSize: 17,
                          fontWeight: 500,
                          color: c.state === 'empty' ? 'var(--fg-3)' : 'var(--fg-1)',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {c.state === 'empty' ? '—' : c.rules}
                      </span>
                      {c.gate === 'abstain' && c.state !== 'empty' && (
                        <span className="mono" style={{ fontSize: 8.5, color: 'var(--abstain-text)' }}>
                          ABST
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span
                        className="label"
                        style={{ fontSize: 8, color: c.state === 'thin' ? 'var(--cov-thin)' : 'var(--fg-3)' }}
                      >
                        {c.state === 'empty' ? 'not covered' : c.state === 'thin' ? 'thin · gap' : `${c.topics} topics`}
                      </span>
                      <DensityBar count={c.rules} max={200} state={c.state} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Dashboard({ ctx }: { ctx: StudioContext }) {
  const { api } = ctx;
  const client = ctx.currentClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute on client + corpus revision
  const cells = useMemo<ClientCell[]>(() => clientCells(api, client), [client.id]);

  const total = cells.length;
  const covered = cells.filter((c) => c.state === 'covered').length;
  const thin = cells.filter((c) => c.state === 'thin').length;
  const empty = cells.filter((c) => c.state === 'empty').length;
  const covPct = total ? Math.round((covered / total) * 100) : 0;
  const rulesServing = cells.reduce((n, c) => n + (c.state === 'empty' ? 0 : c.rules), 0);
  const liveCells = cells.filter((c) => c.state !== 'empty');
  const abstainAvg = liveCells.length
    ? (liveCells.reduce((n, c) => n + c.abstain, 0) / liveCells.length).toFixed(1)
    : '0.0';

  const gaps = gapsFor(cells);
  const recent = auditForClient(api, client, 5);
  const watch = watchForClient(api, client);

  return (
    <Screen className="fade-in">
      <div className="panel" style={{ padding: '18px var(--pad-x)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 10,
                background: 'var(--garnet-muted)',
                border: '1px solid var(--garnet)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span className="serif" style={{ fontSize: 24, color: 'var(--garnet)' }}>
                {client.name.charAt(0)}
              </span>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span className="serif" style={{ fontSize: 26, whiteSpace: 'nowrap' }}>
                  {client.name}
                </span>
                <span className="label">client rule store</span>
              </div>
              <div className="metatext" style={{ marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{client.sector}</span>
                <span className="dimtext">·</span>
                <span>partner since {client.since}</span>
                <span className="dimtext">·</span>
                <span>owner {client.owner}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {client.jurisdictions.map((jc) => (
                  <span key={jc} className="badge b-neutral" style={{ padding: '1px 7px' }}>
                    {jc}
                  </span>
                ))}
                <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 2px' }} />
                {client.domains.map((dk) => (
                  <span key={dk} className="badge b-garnet" style={{ padding: '1px 7px' }}>
                    {domShort(ctx, dk)}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 26, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span className="serif" style={{ fontSize: 40, color: 'var(--garnet)', lineHeight: 1 }}>
                {covPct}%
              </span>
              <span className="label" style={{ fontSize: 8.5 }}>
                coverage
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 22, borderLeft: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', gap: 22 }}>
                <Stat label="Rules serving" value={rulesServing} />
                <Stat label="Covered cells" value={`${covered}/${total}`} tone="pass" />
              </div>
              <div style={{ display: 'flex', gap: 22 }}>
                <Stat label="Open gaps" value={thin + empty} tone={thin + empty ? 'warn' : 'pass'} />
                <Stat label="Abstain rate" value={abstainAvg} unit="%" tone="accent" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Panel
        flush
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Coverage for {client.name}
            <span className="metatext" style={{ fontWeight: 400 }}>
              · {client.jurisdictions.length} jurisdictions × {client.domains.length} domains
            </span>
          </span>
        }
        right={
          <button className="btn btn-ghost btn-sm mono" onClick={() => ctx.goView('coverage')}>
            <Icon name="map" size={12} />
            Full matrix
            <Icon name="arrow" size={11} />
          </button>
        }
      >
        <div style={{ padding: 'var(--pad-x)' }}>
          <ClientSubMatrix ctx={ctx} client={client} />
        </div>
      </Panel>

      <div className="cardgrid" style={{ ['--card' as string]: '380px' }}>
        <Panel flush title="Gaps to close" right={<span className="badge b-warn" style={{ padding: '1px 7px' }}>{gaps.length}</span>}>
          {gaps.length === 0 ? (
            <div style={{ padding: 16 }}>
              <StateBlock kind="empty" title="no open gaps" body="Every cell for this client is covered and answering." />
            </div>
          ) : (
            gaps.map((g) => (
              <div
                key={g.key}
                onClick={() => ctx.openStore(g.key)}
                role="button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px var(--pad-x)',
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--garnet)', width: 22 }}>
                    {g.jc}
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--fg-1)' }}>{domShort(ctx, g.dk)}</span>
                  <CoverageBadge state={g.state} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="metatext mono">{g.state === 'empty' ? 'nothing held' : `${g.rules} rules`}</span>
                  <span
                    className="mono"
                    style={{ fontSize: 12, color: g.noCoverage > 50 ? 'var(--status-fail-text)' : 'var(--status-warn-text)' }}
                  >
                    {g.noCoverage}%
                  </span>
                </div>
              </div>
            ))
          )}
          {ctx.can('author') && gaps.length > 0 && (
            <div style={{ padding: '10px var(--pad-x)' }}>
              <button
                className="btn btn-sm mono"
                onClick={() => {
                  const g = gaps[0];
                  if (g) ctx.createRule(g.jc, g.dk);
                }}
              >
                <Icon name="plus" size={12} />
                Write a rule for the worst gap
              </button>
            </div>
          )}
        </Panel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
          <Panel flush title="Recent changes" right={<span className="metatext mono">this client</span>}>
            {recent.length === 0 ? (
              <div style={{ padding: 16 }}>
                <StateBlock kind="empty" title="no recent changes" />
              </div>
            ) : (
              recent.map((e) => {
                const isRule = api.rule(e.target);
                const src = api.source(e.target);
                return (
                  <div
                    key={e.id}
                    onClick={() => (isRule ? ctx.openRule(e.target) : ctx.openSource(e.target))}
                    role="button"
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '10px var(--pad-x)',
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      alignItems: 'flex-start',
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: KIND_DOT[e.kind] ?? 'var(--fg-3)',
                        marginTop: 5,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--fg-1)' }}>{e.action}</span>
                        <span className="metatext mono" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                          {e.ts.slice(5, 10)}
                        </span>
                      </div>
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--garnet)' }}>
                        {isRule ? e.target : src?.citation ?? e.target}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>

          {watch.length > 0 && (
            <Panel
              flush
              title="Law to revisit"
              right={
                <span className="badge b-warn">
                  <span className="dot pulse" style={{ background: 'var(--status-warn-text)' }} />
                  {watch.length}
                </span>
              }
            >
              {watch.map((s) => (
                <div
                  key={s.id}
                  onClick={() => ctx.openSource(s.id)}
                  role="button"
                  style={{ padding: '10px var(--pad-x)', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Icon name="alert" size={12} color="var(--status-warn-text)" />
                    <span className="mono" style={{ fontSize: 11.5, color: 'var(--garnet)' }}>
                      {s.citation}
                    </span>
                  </div>
                  <div className="metatext" style={{ marginTop: 4 }}>
                    {s.change}
                  </div>
                </div>
              ))}
            </Panel>
          )}
        </div>
      </div>
    </Screen>
  );
}
